/**
 * Admin Rewards — OPEN payout requests + Vault/liabilities truth, sourced from
 * `GET /api/v1/admin/rewards/dashboard`. Admins can RESOLVE or DECLINE an open
 * request with an optional note; the decision is transitioned server-side
 * (OPEN → RESOLVED/REJECTED) and surfaced to the requester as an Activity
 * notification. Settlement of funds still happens off-app (local / relayer) —
 * this only records the operational decision.
 */
import { useState } from "react";
import { rewardsApi, RewardsError, type AdminPayoutRequest, type AdminRewardsDashboard } from "../../lib/rewards-api";
import { fmtDate } from "./adminShared";

function usd(micro: number): string {
  return `${(micro / 1_000_000).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} USDC`;
}

export function AdminRewardsView({
  dashboard,
  onRefresh,
}: {
  dashboard: AdminRewardsDashboard | null;
  onRefresh: () => void;
}) {
  const requests = dashboard?.payout_requests ?? [];
  const vault = dashboard?.vault;

  return (
    <div data-testid="admin-rewards-view" className="grid gap-5 lg:grid-cols-[1.5fr_1fr]">
      {/* ── OPEN PAYOUT REQUESTS ─────────────────────────────────────────── */}
      <section className="panel-chamfer panel-chamfer--sm p-5">
        <header className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <h2 className="m-0 font-display text-[15px] font-semibold text-text-primary">
              Payout requests
            </h2>
            <span
              data-testid="admin-payout-count"
              className="inline-flex items-center justify-center min-w-[1.35rem] h-4 px-1 rounded-sm font-mono text-[9px] font-semibold tabular-nums"
              style={
                requests.length > 0
                  ? { color: "#f0b429", border: "1px solid rgba(240,180,41,0.5)" }
                  : { color: "var(--text-muted)", border: "1px solid var(--border-soft, rgba(255,255,255,0.12))" }
              }
            >
              {dashboard ? requests.length : "…"}
            </span>
          </div>
          <button
            type="button"
            data-testid="admin-rewards-refresh"
            onClick={onRefresh}
            className="inline-flex items-center gap-2 h-7 px-3 font-mono text-[10px] uppercase tracking-[0.16em] text-text-secondary neon-glass pill-ghost"
          >
            <svg width="12" height="12" viewBox="0 0 14 14" fill="none" aria-hidden>
              <path d="M11.5 7a4.5 4.5 0 1 1-1.32-3.18M11.5 2.5V5H9" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Refresh
          </button>
        </header>

        <div className="mt-4">
          {!dashboard ? (
            <div data-testid="admin-rewards-loading" className="py-10 text-center font-mono text-[12px] text-text-muted">
              Loading…
            </div>
          ) : requests.length === 0 ? (
            <div data-testid="admin-payout-empty" className="py-10 text-center font-mono text-[12px] text-text-muted">
              No open payout requests.
            </div>
          ) : (
            <ul className="list-none m-0 p-0 divide-y divide-white/5">
              {requests.map((r) => (
                <PayoutRow key={r.id} req={r} onResolved={onRefresh} />
              ))}
            </ul>
          )}
        </div>

        <p className="mt-4 font-mono text-[10.5px] leading-relaxed text-text-muted">
          Notify-admin only — creates no money and never bypasses the FIFO funding queue.
          Settlement is handled off-app (local / relayer). Resolving or declining here
          records the decision and notifies the requester.
        </p>
      </section>

      {/* ── VAULT & LIABILITIES (context) ────────────────────────────────── */}
      <section className="panel-chamfer panel-chamfer--sm p-5" data-testid="admin-rewards-vault">
        <h2 className="m-0 font-display text-[15px] font-semibold text-text-primary">
          Vault &amp; liabilities
        </h2>
        <div className="mt-4 grid grid-cols-2 gap-3">
          <Stat
            label="Coverage"
            value={vault ? `${vault.coverage_pct}%` : "—"}
            testId="admin-vault-coverage"
            tint={vault && vault.coverage_pct < 100 ? "#f0b429" : "#5ad1c8"}
          />
          <Stat label="Vault balance" value={vault ? usd(vault.balance_micro) : "—"} testId="admin-vault-balance" />
          <Stat label="Funded liability" value={vault ? usd(vault.funded_liability_micro) : "—"} testId="admin-vault-liability" />
          <Stat label="Free surplus" value={vault ? usd(vault.free_surplus_micro) : "—"} testId="admin-vault-surplus" />
          <Stat
            label="Pending funding"
            value={dashboard ? usd(dashboard.liabilities.pending_funding_micro) : "—"}
            testId="admin-vault-pending"
          />
          <Stat
            label="Required top-up"
            value={vault ? usd(vault.required_top_up_micro) : "—"}
            testId="admin-vault-topup"
            tint={vault && vault.required_top_up_micro > 0 ? "#f0b429" : undefined}
          />
        </div>
      </section>
    </div>
  );
}

function PayoutRow({ req, onResolved }: { req: AdminPayoutRequest; onResolved: () => void }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState<null | "RESOLVED" | "REJECTED">(null);
  const [error, setError] = useState<string | null>(null);

  const decide = async (decision: "RESOLVED" | "REJECTED") => {
    setBusy(decision);
    setError(null);
    try {
      await rewardsApi.resolvePayout(req.id, decision, note);
      onResolved();
    } catch (e) {
      const msg =
        e instanceof RewardsError
          ? e.code === "PAYOUT_NOT_OPEN"
            ? "Already processed — refresh the list."
            : e.message
          : e instanceof Error
            ? e.message
            : "Could not update the request.";
      setError(msg);
      setBusy(null);
    }
  };

  return (
    <li data-testid="admin-payout-request-row" className="py-3.5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <code
          data-testid="admin-payout-wallet"
          title={req.wallet}
          className="font-mono text-[12px] text-teal-200 break-all"
        >
          {req.wallet}
        </code>
        <span data-testid="admin-payout-date" className="font-mono text-[11px] text-text-muted">
          {fmtDate(req.created_at)}
        </span>
      </div>

      <div className="mt-2.5 flex flex-wrap items-center gap-2">
        <input
          data-testid="admin-payout-note-input"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          disabled={busy !== null}
          placeholder="Optional note to the requester…"
          className="min-w-[10rem] flex-1 rounded-md border border-border-soft bg-white/[0.02] px-3 py-1.5 font-mono text-[11px] text-text-primary outline-none placeholder:text-text-muted focus:border-[color:var(--neon-teal-ring)] disabled:opacity-60"
        />
        <button
          type="button"
          data-testid="admin-payout-resolve-btn"
          disabled={busy !== null}
          onClick={() => void decide("RESOLVED")}
          className="inline-flex items-center gap-1.5 h-7 px-3 rounded-md font-mono text-[10px] uppercase tracking-[0.14em] text-teal-200 border border-[color:var(--neon-teal-ring)] bg-[color:var(--neon-teal-soft)] hover:brightness-125 disabled:opacity-50 disabled:cursor-not-allowed transition-[filter]"
        >
          {busy === "RESOLVED" ? "Resolving…" : "Resolve"}
        </button>
        <button
          type="button"
          data-testid="admin-payout-reject-btn"
          disabled={busy !== null}
          onClick={() => void decide("REJECTED")}
          className="inline-flex items-center gap-1.5 h-7 px-3 rounded-md font-mono text-[10px] uppercase tracking-[0.14em] text-neon-coral border border-[color:var(--neon-coral-ring)] bg-[color:var(--neon-coral-soft)] hover:brightness-125 disabled:opacity-50 disabled:cursor-not-allowed transition-[filter]"
        >
          {busy === "REJECTED" ? "Declining…" : "Decline"}
        </button>
      </div>

      {error && (
        <p data-testid="admin-payout-row-error" className="mt-2 font-mono text-[10.5px] text-neon-coral">
          {error}
        </p>
      )}
    </li>
  );
}

function Stat({
  label,
  value,
  testId,
  tint,
}: {
  label: string;
  value: string;
  testId: string;
  tint?: string;
}) {
  return (
    <div className="rounded-md border border-border-soft bg-white/[0.02] px-3 py-2.5">
      <div className="font-mono text-[9px] uppercase tracking-[0.14em] text-text-muted">{label}</div>
      <div
        data-testid={testId}
        className="mt-1 font-mono text-[13px] tabular-nums text-text-primary"
        style={tint ? { color: tint } : undefined}
      >
        {value}
      </div>
    </div>
  );
}
